<template>
  <div class="row">
    <div class="col-md-6">
      <div v-if="allowBulkOperations" class="inline-block ml-1">
        <checkbox-list-toggler list="files" :ids="fileIds" label="Select All">
          <span v-if="checkedIds('files').length" class="ml-2">
            (Total size: {{ (selectedTotalSize / 1024 / 1024).toFixed(1) }}MB
            from {{ checkedIds('files').length }} file{{ checkedIds('files').length > 1 ? 's' : '' }})
          </span>
        </checkbox-list-toggler>
      </div>
    </div>

    <div class="col-md-6">
      <form ref="pagination_form" class="form-inline float-md-right" :action="'/claims/' + claimId + '/uploads'">
        <label for="page_size" class="mr-1">Items per page: </label>
        <select name="page_size" v-model="pageSize" class="form-control form-control-sm" @change="$refs.pagination_form.submit()">
          <option v-for="limit in pageLimitOptions" :value="limit">{{ limit }}</option>
        </select>
      </form>
    </div>
  </div>

  <hr class="hr-line-solid my1"/>

  <div class="feed-activity-list">
    <form id="claim-file-bulk-form" ref="claimFileBulkForm" method="POST" :action="bulkRoute">
      <input type="hidden" name="_method" value="PATCH">
      <input type="hidden" name="_token" :value="$csrf_token">

      <template v-if="files.length > 0">

        <div v-for="(upload, index) in files" class="feed-element" :class="{'p-2 bg-light-danger': addedIds.includes(upload.id)}">
          <div class="media mt-0">

            <div v-if="allowBulkOperations" class="align-self-center">
              <div class="form-check form-check-inline mr-2 ml-1">
                <checkbox-toggler list="files" :index="index" name="bulk_uploads_ids[]" :id="upload.id">
                </checkbox-toggler>
              </div>
            </div>

            <div class="align-self-center mr-2">
              <a :href="upload.downloadRoute">
                <i :class="'fa fa-3x ' + upload.icon"></i>
              </a>
            </div>
            <div class="media-body">
              <small class="float-right">{{ upload.createdDiffForHumans }}</small>

              <small class="text-muted"> Uploaded by </small>
              <small><strong><a :href="upload.creatorLink">{{ upload.creatorName }}</a></strong></small>
              <small class="text-muted"> on {{ upload.createdAt }}</small><br/>

              <i v-if="!upload.exists" class="fa fa-exclamation-triangle text-danger"
                 data-toggle="tooltip" title="This document appears to be corrupt or empty"></i>

              <small class="text-muted"> File type: </small>
              <small class="text-muted"><strong>{{ upload.fileType }}</strong></small>
              <small class="text-muted"> File name: </small>
              <small class="text-muted"><strong>{{ upload.name }}</strong></small>

              <template v-if="upload.exists">
                <small class="text-muted"> File size: </small>
                <small class="text-muted"><strong>{{ (upload.size / 1024 / 1024).toFixed(1) }}MB</strong></small>
              </template>

              <template v-if="upload.wfExported">
                <br><small class="text-muted">Exported to WF as <strong>{{ upload.wfType }} report</strong></small>
                <small class="text-muted"> on {{ upload.wfExportedDate }}</small>
              </template>

              <div v-if="upload.hasDescription" class="card card-sm mt-1 bg-well">
                <div class="card-body" v-html="upload.descriptionFormatted"></div>
              </div>
            </div>
          </div>

          <template v-if="allowEditFiles || allowDeleteFiles || allowApproveFiles">
            <hr class="hr-line-dashed my-1 clearfix" />
            <div class="float-right">

              <template v-if="upload.canApprove">
                <approve-file-upload-modal
                  :modal-id="'approveFileModal' + upload.id"
                  title="Approve File"
                  :action="upload.approveRoute"
                  :file-name="upload.name"
                  :file-type="upload.type + ''"
                  :file-types="fileTypes">
                  <a class="btn btn-sm btn-success ml-1" v-modal="'approveFileModal' + upload.id"><i class="fa fa-check-circle-o"></i> Approve </a>
                </approve-file-upload-modal>
              </template>

              <template v-if="upload.canReject">
                <reject-file-upload-modal
                  :modal-id="'rejectFileModal' + upload.id"
                  title="Reject File"
                  :action="upload.rejectRoute"
                  :file-name="upload.name">
                  <a class="btn btn-sm btn-danger ml-1" v-modal="'rejectFileModal' + upload.id"><i class="fa fa-check-circle-o"></i> Reject </a>
                </reject-file-upload-modal>
              </template>

              <template v-if="upload.canReview">
                <span v-if="upload.approved" class="btn-static border-success text-success btn-sm white-bg"><i class="fa fa-lg fa-check-circle m-r-xs"></i>Approved</span>
                <span v-else-if="upload.rejected" class="btn-static border-danger text-danger btn-sm white-bg"><i class="fa fa-lg fa-warning m-r-xs"></i>Rejected</span>
              </template>

              <template v-if="allowEditFiles">
                <edit-file-upload-modal
                  :modal-id="'editFileModal' + upload.id"
                  title="Update File"
                  :action="upload.updateRoute"
                  :file-name="upload.name"
                  :file-type="upload.type + ''"
                  :file-description="upload.description"
                  :file-types="fileTypes">
                  <a class="btn btn-sm btn-white ml-1" v-modal="'editFileModal' + upload.id"><i class="fa fa-pencil-square"></i> Edit </a>
                </edit-file-upload-modal>
              </template>

              <template v-if="allowDeleteFiles && upload.id !== acknowledgementFileId">
                <delete-file-upload-modal
                  :modal-id="'deleteFileModal' + upload.id"
                  title="Delete File"
                  :action="upload.destroyRoute"
                  :file-name="upload.name">
                  <a class="btn btn-sm btn-white ml-1" v-modal="'deleteFileModal' + upload.id"><i class="fa fa-trash"></i> Delete </a>
                </delete-file-upload-modal>
              </template>
            </div>
          </template>
        </div>

        <div v-if="allowBulkOperations" class="mt-3 row no-gutters">
          <div class="col-md-4">
            <claim-uploads-bulk-operations
              ref="bulkOperations"
              operation-field-name="bulk_uploads_operation"
              :claim-id="claimId + ''"
              form-id="claim-file-bulk-form"
              :operations="bulkOperations"
              :wf-report-types="wfReportTypes"
              :upload-objects="uploadObjects"
              :sender-options="senderOptions"
              :sender-signatures="senderSignatures"
              :attachments-max-size="attachmentsMaxSize"
              :allow-manage-lookup-email-item="allowManageLookupEmail">
            </claim-uploads-bulk-operations>
          </div>
        </div>

      </template>
      <template v-else>
        <div v-if="hasActiveFilter" class="fu-no-files text-muted">No files found by request</div>
        <div v-else class="fu-no-files text-muted">No files have been uploaded yet</div>
      </template>
    </form>
  </div>
</template>

<script>
import {mapState} from "pinia";
import {useCheckboxListStore} from "../stores/checkboxList";

export default {

  props: {
    claimId: {type: Number, required: true},
    acknowledgementFileId: {type: Number, default: 0},
    initialFiles: {type: Array, required: true},
    allowEditFiles: {type: Boolean, default: false},
    allowDeleteFiles: {type: Boolean, default: false},
    allowApproveFiles: {type: Boolean, default: false},
    allowBulkOperations: {type: Boolean, default: false},
    allowManageLookupEmail: {type: Boolean, default: false},
    hasActiveFilter: {type: Boolean, default: false},
    bulkRoute: {type: String, required: true},
    fileTypes: {type: Array, required: true},
    bulkOperations: {type: Array, required: true},
    wfReportTypes: {type: Array, required: true},
    senderOptions: {type: Array, required: true},
    senderSignatures: {type: Object, required: false},
    attachmentsMaxSize: {type: Number, default: 0},
    uploadsPageSize: {type: [Number, String], default: 25},
  },

  data() {
    return {
      initialIds: [],
      files: this.initialFiles,
      pageSize: this.uploadsPageSize,
      pageLimitOptions: [10, 25, 50, 100, 'All'],
    }
  },

  computed: {
    ...mapState(useCheckboxListStore, ['checkedIds']),

    fileIds() {
      return this.files.map((file) => file.id);
    },

    addedIds() {
      return this.fileIds.filter(fid => !this.initialIds.includes(fid));
    },

    uploadObjects() {
      return this.files.map(function(file) {
        return {
          "id": file.id,
          "name": file.name,
          "file_type": file.type
        };
      });
    },

    selectedTotalSize() {
      let totalSize = 0;
      this.files
        .filter((file) => file.exists)
        .filter((file) => this.checkedIds('files').includes(file.id))
        .forEach((file) => totalSize += file.size);
      return totalSize;
    },

  },

  mounted() {
    this.initialIds = _.cloneDeep(this.initialFiles.map((file) => file.id));
  }

}
</script>
